import { formatInterval } from "@/components/playlist/detail/Common";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatLocationType } from "@/lib/format";
import { Play } from "@/queries/plays";
import { CircleX } from "lucide-react";

const StatusIndicator = ({
  label,
  timestamp,
  override_timestamp,
}: {
  label: string;
  timestamp: string | null;
  override_timestamp?: string | null;
}) => (
  <div className="flex items-center space-x-1">
    <div
      className={`w-3 h-3 rounded-full border ${timestamp ? "bg-green-500" : "bg-gray-300"}`}
    />
    <div className="flex flex-col">
      <span className="text-xs font-mono">{label}</span>
      {label === "Schedule" && !!override_timestamp && (
        <span className="text-xs text-slate-400">
          {new Date(override_timestamp)
            .toLocaleString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              day: "2-digit",
              month: "2-digit",
            })
            .replace(",", "")}
        </span>
      )}
    </div>{" "}
  </div>
);

// PlayInformation.tsx
interface PlayInformationProps {
  play: Play;
  onClose?: () => void;
}

export function PlayInformation({ play, onClose }: PlayInformationProps) {
  return (
    <>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">{play.title}</CardTitle>
            {play.read_only && (
              <span className="px-2 py-1 text-xs font-semibold rounded bg-slate-700 text-slate-300">
                Archived
              </span>
            )}
          </div>
          {onClose && (
            <Button variant="ghost" onClick={onClose}>
              <CircleX />
            </Button>
          )}
        </div>
        <CardDescription className="text-slate-300">
          {play.message}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex space-x-4">
          <StatusIndicator
            label="Schedule"
            timestamp={play.scheduled_at}
            override_timestamp={play.override_scheduled_at}
          />
          <StatusIndicator label="Execute" timestamp={play.actioned_at} />
          <StatusIndicator label="Complete" timestamp={play.completed_at} />
        </div>
        <div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-slate-300">Location:</p>
              <p className="text-slate-300">
                {formatLocationType(play.location_type)} {play.location_details}
              </p>
            </div>
            {!play.adhoc && (
              <div>
                <p className="font-semibold text-slate-300">Pax:</p>
                <p className="text-slate-300 font-mono">
                  <span className="text-red-700">{play.observed_value}</span>
                  <span className="text-slate-600 opacity-70">
                    {" "}
                    (&gt; {play.threshold_value})
                  </span>
                </p>
              </div>
            )}
            <div>
              <p className="font-semibold">Duration:</p>
              <p className="text-slate-300">
                {play.start_interval === play.end_interval ? (
                  formatInterval(play.start_interval)
                ) : (
                  <>
                    {formatInterval(play.start_interval)} to{" "}
                    {formatInterval(play.end_interval)}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </>
  );
}
